import React, { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import cn from 'classnames'
import { Coins, Copy, Info, Link, Share2 } from 'lucide-react'
import { EmailIcon, FacebookIcon, RedditIcon, TelegramIcon, TwitterIcon } from 'next-share'
import Tray from 'components/shared/Tray'
import ShareRewardsInfoTray from 'components/modals/ShareRewardsInfoTray'
import styles from 'components/shared/share-tray.module.scss'
import { insertToClipboard } from 'utils'
import { isMobile } from 'lib/device'
import { hasNativeCapability, shareTextOrUrlNative } from 'utils/nativeShare'
import { buildShareDestination, resolveAnswerShareSurface } from 'utils/shareUtils'

const SHARE_CARD_PRESET = 'landscape'

// Clipboard images must be PNG (Chromium rejects image/jpeg ClipboardItems),
// so the JPEG card re-encodes through a canvas. The ClipboardItem takes the
// encode promise directly: Safari only honors writes that start inside the
// user gesture's task.
const copyShareCardToClipboard = async jpegBlob => {
    if (
        typeof navigator === 'undefined' ||
        !navigator.clipboard?.write ||
        typeof ClipboardItem === 'undefined'
    ) {
        return false
    }

    try {
        const pngBlob = (async () => {
            const bitmap = await createImageBitmap(jpegBlob)
            try {
                const canvas = document.createElement('canvas')
                canvas.width = bitmap.width
                canvas.height = bitmap.height
                canvas.getContext('2d').drawImage(bitmap, 0, 0)
                return await new Promise((resolve, reject) => {
                    canvas.toBlob(
                        blob => (blob ? resolve(blob) : reject(new Error('PNG encode failed'))),
                        'image/png',
                    )
                })
            } finally {
                bitmap.close?.()
            }
        })()
        await navigator.clipboard.write([new ClipboardItem({ 'image/png': pngBlob })])
        return true
    } catch (error) {
        console.error('Failed to copy image card to clipboard', error)
        return false
    }
}

export default function ShareTray({
    canShareImageCard = false,
    closeButtonTestId = 'share-tray-close',
    dialogTestId = 'share-tray',
    isOpen,
    linkReady = true,
    onDismiss,
    overlayClassName = undefined,
    rewardEnabled = false,
    shareCardId,
    shareCardUrl,
    shareKind = 'post',
    shareText = '',
    shareTitle = '',
    shareUrl,
}) {
    const [shareLinkCopied, setShareLinkCopied] = useState(false)
    const [rewardInfoOpen, setRewardInfoOpen] = useState(false)
    const [shareCardSharing, setShareCardSharing] = useState(false)
    const [shareCardCopied, setShareCardCopied] = useState(false)
    const [shareCardError, setShareCardError] = useState(null)
    const [shareCardActionError, setShareCardActionError] = useState(null)
    const [shareCardBlob, setShareCardBlob] = useState(null)
    const [shareCardPreviewUrl, setShareCardPreviewUrl] = useState(null)
    const [shareCardPreviewLoading, setShareCardPreviewLoading] = useState(false)
    const canUseMobileNativeShare = hasNativeCapability('nativeShare')
    const canUseNativeShare =
        canUseMobileNativeShare ||
        (typeof navigator !== 'undefined' && typeof navigator.share === 'function')
    // On phones the OS share sheet is the destination picker, so the tray
    // collapses to one Share action (the card when it exists, text otherwise)
    // plus copy link. Desktop keeps the per-destination rows.
    const isMobileNativeFlow = canUseMobileNativeShare || (isMobile && canUseNativeShare)
    const normalizedShareKind =
        String(shareKind || 'post')
            .trim()
            .toLowerCase() || 'post'
    const shouldShowImageCard = canShareImageCard && !!shareCardUrl
    const shareSurface = resolveAnswerShareSurface({
        isMobileNativeFlow,
        canUseNativeShare,
        canShareImageCard: shouldShowImageCard,
        shareCardPreviewLoading,
        shareCardReady: !!shareCardBlob,
        shareCardFailed: !!shareCardError,
        shareCardSharing,
    })

    useEffect(() => {
        setShareLinkCopied(false)
        setShareCardSharing(false)
        setShareCardCopied(false)
        setShareCardError(null)
        setShareCardActionError(null)
        setShareCardBlob(null)
        setShareCardPreviewUrl(null)
        setShareCardPreviewLoading(false)

        if (!isOpen || !shouldShowImageCard || !shareCardUrl) return

        setShareCardPreviewLoading(true)
        const requestController = new AbortController()
        let objectUrl = null
        let cancelled = false

        fetch(shareCardUrl, {
            credentials: 'same-origin',
            headers: { Accept: 'image/jpeg' },
            signal: requestController.signal,
        })
            .then(response => {
                if (!response.ok) {
                    throw new Error(`Share card render failed (${response.status})`)
                }
                return response.blob()
            })
            .then(blob => {
                if (cancelled) return
                if (!blob.type.startsWith('image/')) {
                    throw new Error('Share card response was not an image')
                }

                objectUrl = URL.createObjectURL(blob)
                setShareCardBlob(blob)
                setShareCardPreviewUrl(objectUrl)
            })
            .catch(error => {
                if (cancelled || error?.name === 'AbortError') return
                console.error('Failed to load image card preview', error)
                setShareCardPreviewLoading(false)
                setShareCardError('Could not load that card. Please try again.')
            })

        return () => {
            cancelled = true
            requestController.abort()
            if (objectUrl) URL.revokeObjectURL(objectUrl)
        }
    }, [isOpen, shouldShowImageCard, shareCardUrl])

    if (!isOpen || !shareUrl) return null

    const handleShareOptionClick = ev => {
        ev.stopPropagation()
        window.setTimeout(() => {
            onDismiss?.()
        }, 0)
    }

    const getShareDestination = destination =>
        buildShareDestination({
            destination,
            shareUrl,
            shareText,
            shareTitle,
        })

    const openShareDestination = (ev, destination) => {
        handleShareOptionClick(ev)
        window.open(getShareDestination(destination).url, '_blank', 'noopener,noreferrer')
    }

    const shareWithNativeSheet = async () => {
        const payload = getShareDestination('native').payload

        if (canUseMobileNativeShare) {
            await shareTextOrUrlNative(payload)
        } else {
            await navigator.share(payload)
        }
    }

    const handleNativeShare = async ev => {
        ev.preventDefault()
        ev.stopPropagation()

        if (!canUseNativeShare) return

        try {
            await shareWithNativeSheet()
            onDismiss?.()
        } catch (error) {
            if (error?.name === 'AbortError') return
        }
    }

    const handleCopyShareLink = ev => {
        ev.preventDefault()
        ev.stopPropagation()

        insertToClipboard(shareUrl)
        setShareLinkCopied(true)
        window.setTimeout(() => {
            setShareLinkCopied(false)
        }, 1500)
    }

    const handleEmailShare = ev => {
        ev.preventDefault()
        ev.stopPropagation()

        onDismiss?.()
        window.location.href = getShareDestination('email').url
    }

    const handleShareCard = async ev => {
        ev.preventDefault()
        ev.stopPropagation()

        if (!shareCardUrl || shareCardSharing) return

        setShareCardSharing(true)
        setShareCardActionError(null)

        try {
            if (isMobileNativeFlow) {
                // Native destinations fetch the rendered card from og:image,
                // so opening the share sheet never waits for the tray preview.
                await shareWithNativeSheet()
                onDismiss?.()
                return
            }

            if (!shareCardBlob || shareCardPreviewLoading) {
                throw new Error('Share card preview is still loading')
            }

            const idSuffix = shareCardId ? `-${shareCardId}` : ''
            const fileName = `treechat-${normalizedShareKind}-${SHARE_CARD_PRESET}${idSuffix}.jpg`

            // Desktop copies the card and keeps the tray open for feedback;
            // download only when the clipboard cannot accept images.
            if (await copyShareCardToClipboard(shareCardBlob)) {
                setShareCardCopied(true)
                window.setTimeout(() => setShareCardCopied(false), 1500)
                return
            }

            const downloadUrl = URL.createObjectURL(shareCardBlob)
            const downloadLink = document.createElement('a')
            downloadLink.href = downloadUrl
            downloadLink.download = fileName
            document.body.appendChild(downloadLink)
            downloadLink.click()
            downloadLink.remove()
            window.setTimeout(() => URL.revokeObjectURL(downloadUrl), 10000)
            onDismiss?.()
        } catch (error) {
            if (error?.name === 'AbortError') return

            console.error('Failed to share image card', error)
            setShareCardActionError(
                `Could not share that ${normalizedShareKind}. Please try again.`,
            )
        } finally {
            setShareCardSharing(false)
        }
    }

    if (typeof document === 'undefined') return null

    return createPortal(
        <Tray
            title={`Share ${normalizedShareKind}`}
            onDismiss={onDismiss}
            className={overlayClassName}
            trayClassName={cn(styles.shareTray, styles.shareTrayDialog)}
            bodyClassName={styles.shareTrayBody}
            closeLabel="Close share options"
            preventEscClose={rewardInfoOpen}
            preventBackdropClose={rewardInfoOpen}
            dialogTestId={dialogTestId}
            closeButtonTestId={closeButtonTestId}
        >
            <div
                className={cn(styles.shareOptions, !linkReady && styles.shareOptionsPreparing)}
                aria-busy={!linkReady}
            >
                {!linkReady && (
                    <div
                        className={cn(styles.shareRewardCallout, styles.shareLinkPreparing)}
                        role="status"
                    >
                        Preparing your share link…
                    </div>
                )}
                {linkReady && rewardEnabled && (
                    <button
                        type="button"
                        className={styles.shareRewardPill}
                        data-testid="share-reward-pill"
                        onClick={() => setRewardInfoOpen(true)}
                    >
                        <span className={styles.shareRewardPillIcon}>
                            <Coins size={16} aria-hidden="true" />
                        </span>
                        <span className={styles.shareRewardPillText}>
                            <strong>Earn 1¢</strong> for every new visitor you bring to Treechat
                        </span>
                        <Info size={15} className={styles.shareRewardPillInfo} aria-hidden="true" />
                    </button>
                )}
                <ShareRewardsInfoTray
                    isOpen={rewardInfoOpen}
                    onDismiss={() => setRewardInfoOpen(false)}
                />
                {shouldShowImageCard && (
                    <div className={styles.shareCardSection}>
                        {shareSurface.showDesktopDestinations && (
                            <div className={styles.shareCardHeading}>share an image</div>
                        )}
                        {shareSurface.shareCardHeroVisible && (
                            <button
                                type="button"
                                className={styles.shareCardHero}
                                disabled={shareSurface.shareCardHeroDisabled}
                                aria-busy={shareCardSharing || shareCardPreviewLoading}
                                aria-label={
                                    isMobileNativeFlow
                                        ? `Share this ${normalizedShareKind}`
                                        : `Copy the image card for this ${normalizedShareKind}`
                                }
                                data-testid="share-card-primary-action"
                                onClick={handleShareCard}
                            >
                                {shareCardPreviewUrl && (
                                    <img
                                        className={cn(
                                            styles.shareCardHeroImage,
                                            !shareCardPreviewLoading &&
                                                styles.shareCardHeroImageReady,
                                        )}
                                        src={shareCardPreviewUrl}
                                        alt={`Landscape ${normalizedShareKind} share card preview`}
                                        onLoad={() => setShareCardPreviewLoading(false)}
                                        onError={() => {
                                            setShareCardPreviewLoading(false)
                                            setShareCardError(
                                                'Could not load that card. Please try again.',
                                            )
                                        }}
                                    />
                                )}
                                {shareCardPreviewLoading && (
                                    <span
                                        className={styles.shareCardHeroShimmer}
                                        aria-hidden="true"
                                    />
                                )}
                                <span className={styles.shareCardHeroPill}>
                                    {shareCardSharing ? (
                                        isMobileNativeFlow ? (
                                            'sharing…'
                                        ) : (
                                            'copying…'
                                        )
                                    ) : isMobileNativeFlow ? (
                                        <>
                                            <Share2 size={18} aria-hidden="true" />
                                            share
                                        </>
                                    ) : shareCardPreviewLoading ? (
                                        'loading preview…'
                                    ) : shareCardCopied ? (
                                        'copied!'
                                    ) : (
                                        <>
                                            <Copy size={18} aria-hidden="true" />
                                            copy image
                                        </>
                                    )}
                                </span>
                            </button>
                        )}
                        {(shareCardError || shareCardActionError) && (
                            <div className={styles.shareCardError} role="status">
                                {shareCardError || shareCardActionError}
                            </div>
                        )}
                    </div>
                )}

                {shareSurface.showNativeFallback && (
                    <button
                        type="button"
                        className={styles.shareOptionButton}
                        onClick={handleNativeShare}
                    >
                        <div className={cn(styles.shareOptionIcon, styles.shareOptionIconRound)}>
                            <Share2 size={20} />
                        </div>
                        <span className={styles.shareOptionLabel}>share</span>
                    </button>
                )}

                <button
                    type="button"
                    className={styles.shareOptionButton}
                    onClick={handleCopyShareLink}
                >
                    <div className={cn(styles.shareOptionIcon, styles.shareOptionIconRound)}>
                        <Link size={20} />
                    </div>
                    <span className={styles.shareOptionLabel}>
                        {shareLinkCopied ? 'link copied!' : 'copy link'}
                    </span>
                </button>

                {shareSurface.showDesktopDestinations && (
                    <button
                        type="button"
                        className={styles.shareOptionButton}
                        onClick={ev => openShareDestination(ev, 'x')}
                    >
                        <TwitterIcon size={36} round className={styles.shareOptionIcon} />
                        <span className={styles.shareOptionLabel}>share to x</span>
                    </button>
                )}

                {shareSurface.showDesktopDestinations && (
                    <button
                        type="button"
                        className={styles.shareOptionButton}
                        onClick={ev => openShareDestination(ev, 'facebook')}
                    >
                        <FacebookIcon size={36} round className={styles.shareOptionIcon} />
                        <span className={styles.shareOptionLabel}>share to facebook</span>
                    </button>
                )}

                {shareSurface.showDesktopDestinations && (
                    <button
                        type="button"
                        className={styles.shareOptionButton}
                        onClick={ev => openShareDestination(ev, 'reddit')}
                    >
                        <RedditIcon size={36} round className={styles.shareOptionIcon} />
                        <span className={styles.shareOptionLabel}>share to reddit</span>
                    </button>
                )}

                {shareSurface.showDesktopDestinations && (
                    <button
                        type="button"
                        className={styles.shareOptionButton}
                        onClick={ev => openShareDestination(ev, 'telegram')}
                    >
                        <TelegramIcon size={36} round className={styles.shareOptionIcon} />
                        <span className={styles.shareOptionLabel}>share to telegram</span>
                    </button>
                )}

                {shareSurface.showDesktopDestinations && (
                    <button
                        type="button"
                        className={styles.shareOptionButton}
                        onClick={handleEmailShare}
                    >
                        <EmailIcon size={36} round className={styles.shareOptionIcon} />
                        <span className={styles.shareOptionLabel}>share via email</span>
                    </button>
                )}

                {shareSurface.showDesktopDestinations && canUseNativeShare && (
                    <button
                        type="button"
                        className={styles.shareOptionButton}
                        onClick={handleNativeShare}
                    >
                        <div className={cn(styles.shareOptionIcon, styles.shareOptionIconRound)}>
                            <Share2 size={20} />
                        </div>
                        <span className={styles.shareOptionLabel}>share with device</span>
                    </button>
                )}
            </div>
        </Tray>,
        document.body,
    )
}
